import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BarChart2, RefreshCw, AlertTriangle, ArrowUpRight, Sparkles, Lock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { api, useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { toast } from 'sonner';

const METRIC_LABELS = {
  keywords: 'Keywords',
  target_pages: 'Target Pages',
  blog_posts: 'Blog Posts',
  articles: 'Articles',
  backlinks: 'Backlinks',
  blog_links: 'Blog Links',
  social_bookmarks: 'Social Bookmarks',
  micro_blogs: 'Micro Blogs',
  image_sharing: 'Image Sharing',
  classified_submissions: 'Classified Submissions',
  ppt_submissions: 'PPT Submissions',
  location_optimizations: 'Location Optimizations',
  local_listings: 'Local Listings',
  facebook_posts: 'Facebook Posts',
  instagram_posts: 'Instagram Posts',
  twitter_posts: 'Twitter Posts',
  linkedin_posts: 'LinkedIn Posts',
  web2_profiles: 'Web 2.0 Profiles',
  article_marketing: 'Article Marketing',
  gbp_posts: 'GBP Posts',
  ai_recommendations: 'AI Recommendations',
};

const PRIVILEGED_ROLES = new Set([
  'admin',
  'project_manager',
  'content_account',
  'creative_account',
  'execution_team',
]);

function UsageBar({ used, limit }) {
  if (limit === 0) {
    // Only shown to privileged/admin roles (clients never see limit=0 metrics in the grid)
    return (
      <div className="flex items-center gap-2 text-sm text-slate-400 dark:text-slate-500">
        <Lock className="h-4 w-4" />
        Not in plan
      </div>
    );
  }

  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-500' : 'bg-green-500';

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
        <span>{used} used</span>
        <span>{limit} limit</span>
      </div>
      <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full transition-all ${color}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export default function PackageUsagePage() {
  const { user } = useAuth();
  const isPrivilegedUser = PRIVILEGED_ROLES.has(user?.role);
  const { projects, selectedProjectId } = useProject();
  const [usageProjectId, setUsageProjectId] = useState(selectedProjectId || '');
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!projects.length) return;

    const projectIds = projects.map(p => String(p.project_id));
    if (usageProjectId && projectIds.includes(usageProjectId)) return;

    const globalProjectId = String(selectedProjectId || '');
    const fallbackProjectId = projectIds.includes(globalProjectId)
      ? globalProjectId
      : String(projects[0].project_id);

    setUsageProjectId(fallbackProjectId);
  }, [projects, selectedProjectId, usageProjectId]);

  useEffect(() => {
    if (usageProjectId) {
      fetchUsage(usageProjectId);
    } else {
      setUsage(null);
    }
  }, [usageProjectId]);

  async function fetchUsage(projectId) {
    setLoading(true);
    try {
      const r = await api.get(`/package-usage/${projectId}`);
      setUsage(r.data);
    } catch (e) {
      toast.error('Failed to load package usage');
    } finally {
      setLoading(false);
    }
  }

  const packageColors = {
    basic: 'text-slate-600 dark:text-slate-300',
    silver: 'text-slate-500 dark:text-slate-200',
    gold: 'text-yellow-600 dark:text-yellow-400',
    platinum: 'text-blue-600 dark:text-blue-400',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <BarChart2 className="h-6 w-6 text-blue-500 dark:text-blue-400" />
            {isPrivilegedUser ? 'Usage Monitoring' : 'Package Usage'}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
            {isPrivilegedUser ? 'Monthly usage monitoring by project' : 'Monthly quota consumption by project'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => usageProjectId && fetchUsage(usageProjectId)}
          disabled={loading}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Project selector */}
      <div className="flex gap-3 items-center">
        <label className="text-sm text-slate-500 dark:text-slate-400">Project:</label>
        <select
          value={usageProjectId}
          onChange={e => setUsageProjectId(e.target.value)}
          className="h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 text-sm text-slate-900 dark:text-slate-100 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]"
        >
          {!usageProjectId && (
            <option value="" disabled>Select a project</option>
          )}
          {projects.map(p => (
            <option key={p.project_id} value={String(p.project_id)}>
              {p.name}
            </option>
          ))}
        </select>
      </div>

      {usage && (
        <>
          {!isPrivilegedUser && (
            <div className="flex items-center gap-3">
              <span className="text-slate-500 dark:text-slate-400 text-sm">Package:</span>
              {usage.package_type ? (
                <span className={`font-semibold capitalize text-sm ${packageColors[usage.package_type] || 'text-slate-900 dark:text-white'}`}>
                  {usage.package_type}
                </span>
              ) : (
                <span className="text-slate-400 dark:text-slate-500 text-sm italic">No package assigned</span>
              )}
              <span className="text-slate-400 dark:text-slate-500 text-xs">
                {new Date(usage.year, usage.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
              </span>
            </div>
          )}

          {/* No-plan banner */}
          {!isPrivilegedUser && !usage.package_type && (
            <Card className="border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                      No plan assigned — execution is blocked.
                    </p>
                    <p className="text-xs text-amber-800 dark:text-amber-300 mt-0.5">
                      Choose a package to start consuming features.
                    </p>
                  </div>
                </div>
                <Link to="/dashboard/billing">
                  <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white">
                    Select a plan <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Quota-exhausted banner: show when any metric is at 100% */}
          {!isPrivilegedUser && usage.package_type && usage.metrics?.some(m => m.limit > 0 && m.used >= m.limit) && (
            <Card className="border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800">
              <CardContent className="p-4 flex items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-900 dark:text-red-200">
                      One or more limits reached.
                    </p>
                    <p className="text-xs text-red-800 dark:text-red-300 mt-0.5">
                      Upgrade your plan to keep executing this month.
                    </p>
                  </div>
                </div>
                <Link to="/dashboard/billing">
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white">
                    Upgrade plan <ArrowUpRight className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          )}

          {/* Per-project quota note */}
          <p className="text-xs text-slate-400 dark:text-slate-500 italic">
            {isPrivilegedUser
              ? 'Usage is tracked per project for monitoring and operations.'
              : 'Quotas apply per project. A user with multiple projects has independent monthly quotas for each.'}
          </p>

          {/* Metrics grid */}
          {usage.metrics?.length > 0 ? (
            (() => {
              // For client users, split metrics into included (limit > 0) and
              // excluded (limit === 0 — not part of their plan). Privileged roles
              // see everything so they can monitor all counters.
              const includedMetrics = isPrivilegedUser
                ? usage.metrics
                : usage.metrics.filter(m => m.limit !== 0);
              const lockedMetrics = isPrivilegedUser
                ? []
                : usage.metrics.filter(m => m.limit === 0);

              return (
                <>
                  {includedMetrics.length > 0 ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {includedMetrics.map(m => (
                        <motion.div
                          key={m.metric}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                        >
                          <Card>
                            <CardContent className="p-4 space-y-3">
                              <p className="text-sm font-medium text-slate-900 dark:text-white">
                                {METRIC_LABELS[m.metric] || m.metric}
                              </p>
                              <UsageBar used={m.used} limit={m.limit} />
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <Card>
                      <CardContent className="p-8 text-center text-slate-500 dark:text-slate-400">
                        No active quota metrics for this package. Contact support if this seems incorrect.
                      </CardContent>
                    </Card>
                  )}

                  {/* Locked / upgrade-eligible features — collapsed summary for clients */}
                  {lockedMetrics.length > 0 && (
                    <Card className="border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-3 mb-3">
                          <div className="flex items-center gap-2">
                            <Lock className="h-4 w-4 text-slate-400 dark:text-slate-500" />
                            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">
                              {lockedMetrics.length} feature{lockedMetrics.length !== 1 ? 's' : ''} not included in your plan
                            </p>
                          </div>
                          <Link to="/dashboard/billing">
                            <Button variant="outline" size="sm" className="text-xs">
                              Upgrade to unlock <ArrowUpRight className="h-3 w-3 ml-1" />
                            </Button>
                          </Link>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {lockedMetrics.map(m => (
                            <span
                              key={m.metric}
                              className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-200 dark:bg-slate-700 text-xs text-slate-500 dark:text-slate-400"
                            >
                              <Lock className="h-3 w-3" />
                              {METRIC_LABELS[m.metric] || m.metric}
                            </span>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </>
              );
            })()
          ) : (
            <Card>
              <CardContent className="p-8 text-center text-slate-500 dark:text-slate-400">
                {isPrivilegedUser
                  ? 'No usage counters available for this project yet.'
                  : 'No package assigned to this project. Set a package tier to see quota tracking.'}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {!usage && !loading && (
        <Card>
          <CardContent className="p-12 text-center text-slate-500 dark:text-slate-400">
            Select a project to view its package usage.
          </CardContent>
        </Card>
      )}

      {loading && (
        <Card>
          <CardContent className="p-12 text-center text-slate-500 dark:text-slate-400">
            <RefreshCw className="h-5 w-5 animate-spin mx-auto mb-2" />
            Loading usage data...
          </CardContent>
        </Card>
      )}
    </div>
  );
}
